import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate, Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AdminLayoutShell from "../../admin/navigation/AdminLayoutShell";
import { getUserTypeName } from "../../admin/navigation/adminNavigation";
import {
  partnerSidebarFooter,
  PARTNER_PANEL_HOME,
  getPartnerBreadcrumbs,
  getPartnerPageTitle,
  getPartnerSidebar,
  isHiddenForInterior,
  isPartnerRoot,
  INTERIOR_CATALOG_PATH,
} from "./partnerNavigation";
import { useResolvedPartnerService } from "../../utils/partnerService";
import CustomerBreadcrumbs from "../customer/CustomerBreadcrumbs";
import { checkPartnerAccess } from "../../guards/partnerGuard";
import PartnerExpiredPlanModal from "./PartnerExpiredPlanModal";
import PartnerDocumentGuardOverlay from "./PartnerDocumentGuardOverlay";
import "../../admin/navigation/AdminShell.css";
import "./PartnerGuardModal.css";

const PartnerDashboardLayout = ({ children }) => {
  const authState = useSelector((state) => state.auth);
  const authUser = authState?.user;
  const role = getUserTypeName(authState);

  const { pathname } = useLocation();
  const { kind: serviceKind } = useResolvedPartnerService();

  // Empty while the role or the service type is unknown (no flash of the wrong menu), and for any role that
  // is not PARTNER, so another role never sees this menu (or the TOOLS group).
  const sections = useMemo(() => getPartnerSidebar(role, serviceKind), [role, serviceKind]);

  // Parent route used when there is no in-app history to go back to (e.g. a direct deep link)
  const getBackTarget = useCallback((pathname) => {
    if (isPartnerRoot(pathname)) return null;
    const crumbs = getPartnerBreadcrumbs(pathname);
    const parent = [...crumbs].reverse().find((crumb, i) => i > 0 && crumb.to);
    return parent?.to ?? PARTNER_PANEL_HOME;
  }, []);

  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id || authUser?.id;

  const [guardState, setGuardState] = useState({
    loading: true,
    allowAccess: true,
    isSubExpired: false,
    isSubPending: false,
    isDocMissing: false,
    subscription: null,
    docStatus: {
      isComplete: true,
      requiredDocs: ["aadhar", "pan"],
      missingDocs: [],
      uploadedDocsMap: {},
      serviceId: "1",
    },
    user: authUser?.user || authUser?.userDetails || null,
    assetUrl: "",
  });

  const evaluateAccess = useCallback(async () => {
    if (!token) return;
    try {
      const result = await checkPartnerAccess({ token, userId });
      const docMissing = !result.docStatus?.isComplete;

      setGuardState({
        loading: false,
        allowAccess: result.allowAccess,
        isSubExpired: result.isSubExpired,
        isSubPending: result.isSubPending,
        isDocMissing: docMissing,
        subscription: result.subscription,
        docStatus: result.docStatus,
        user: result.user || authUser?.user || authUser?.userDetails,
        assetUrl: result.assetUrl,
      });
    } catch (err) {
      console.warn("Partner layout access evaluation warning:", err);
      setGuardState((prev) => ({ ...prev, loading: false }));
    }
  }, [token, userId, authUser]);

  useEffect(() => {
    evaluateAccess();
  }, [evaluateAccess]);

  const shouldBlur = guardState.isSubExpired;

  // Interior Design partners have no property pages: old links and typed URLs land on the Catalog.
  if (serviceKind === "interior" && isHiddenForInterior(pathname)) {
    return <Navigate to={INTERIOR_CATALOG_PATH} replace />;
  }

  return (
    <AdminLayoutShell
      sections={sections}
      footerItems={sections.length ? partnerSidebarFooter : []}
      staticHeadings
      homePath={PARTNER_PANEL_HOME}
      logoutRedirect="/login"
      titleGetter={getPartnerPageTitle}
      getBackTarget={getBackTarget}
    >
      <CustomerBreadcrumbs getBreadcrumbs={getPartnerBreadcrumbs} />
      <div className={`partner-dash-content-wrapper ${shouldBlur ? "partner-dash-blurred" : ""}`}>
        {serviceKind === "unknown" ? null : children ?? <Outlet />}
      </div>
      {/* back/forward return to the same scroll position */}
      <ScrollRestoration />

      {/* Expired Plan Pop Modal */}
      {guardState.isSubExpired && (
        <PartnerExpiredPlanModal
          visible={guardState.isSubExpired}
          subscription={guardState.subscription}
          token={token}
          onSuccess={evaluateAccess}
        />
      )}

      {/* Mandatory Documents Upload Guard Overlay */}
      {!guardState.isSubExpired && guardState.isDocMissing && (
        <PartnerDocumentGuardOverlay
          visible={guardState.isDocMissing}
          userData={guardState.user}
          uploadedDocsMap={guardState.docStatus?.uploadedDocsMap || {}}
          assetUrl={guardState.assetUrl}
          token={token}
          userId={userId}
          onRefresh={evaluateAccess}
        />
      )}
    </AdminLayoutShell>
  );
};

export default PartnerDashboardLayout;
